import { TemplatePtAPI } from "@/service/api/template_trace";
import {
  Button,
  type ButtonProps,
  Checkbox,
  Modal,
  Spinner,
  useOverlayState,
} from "@heroui/react";
import { ArrowDownToLine } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "@repo/context/auth/auth";
import { isRestricted } from "../-utils/utils";
import BridgeHelper from "@/utils/rn-bridge/bridge-helper";
import { NavCode } from "@/utils/rn-bridge/nav-code";

export const DownloadModal = ({
  SendId,
  BtnProps,
}: {
  SendId: string;
  BtnProps: ButtonProps;
}) => {
  const modal = useOverlayState();
  const { userInfo } = useAuth();
  const [agreeTerms, setAgreeTerms] = useState({
    terms: false,
    responsibility: false,
  });
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  const isUserRestricted = userInfo ? isRestricted(userInfo) : false;

  const handleDownload = async (type: "psd" | "png") => {
    if (isDownloading) return;
    if (!SendId) {
      toast.error("操作非法，请刷新页面重试");
      return;
    }

    setIsDownloading(type);
    try {
      const tokenResponse = await TemplatePtAPI.getDownloadToken(SendId, type);

      if (!tokenResponse.data || tokenResponse.data.length === 0) {
        toast.error("获取下载链接失败，请稍后重试");
        return;
      }

      for (const file of tokenResponse.data) {
        BridgeHelper.sendMessage({
          nav_code: NavCode.OPEN_PHONE_BROWSER,
          data: {
            url: file.path,
          },
        });
      }
    } catch (error: unknown) {
      console.error("下载失败:", error);
      const msg = (error as { msg?: string })?.msg || "下载失败，请稍后重试";
      toast.error(msg);
    } finally {
      setIsDownloading(null);
    }
  };

  const DownloadComponent = ({
    title,
    description,
    onDownload,
    disabled,
    isLoading,
  }: {
    title: string;
    description: string;
    onDownload: () => void;
    disabled: boolean;
    isLoading: boolean;
  }) => {
    return (
      <div className="flex flex-1 flex-col items-center justify-between gap-2.5 rounded-2xl border border-gray-200 p-4 transition-colors hover:border-primary-200 hover:bg-primary-50">
        <div className="flex flex-col items-center justify-start gap-2.5">
          <div className="text-lg font-bold">{title}</div>
          <div className="text-sm text-gray-500">{description}</div>
        </div>
        <Button
          className="rounded-full bg-primary text-white"
          onPress={onDownload}
          size="sm"
          isDisabled={disabled || isLoading}
          isPending={isLoading}
        >
          {({ isPending }) => (
            <>
              {isPending ? <Spinner size="sm" color="current" /> : <ArrowDownToLine size={16} />}
              {isPending ? "下载中..." : ""}
            </>
          )}
        </Button>
      </div>
    );
  };

  return (
    <Modal>
      <Button
        {...BtnProps}
        onPress={modal.open}
        isDisabled={BtnProps?.isDisabled || isUserRestricted}
      >
        <ArrowDownToLine size={18} />
        <span>下载</span>
      </Button>
      <Modal.Backdrop
        isOpen={modal.isOpen}
        onOpenChange={(open) => {
          if (!open && !isDownloading) modal.setOpen(false);
        }}
        isDismissable={false}
      >
        <Modal.Container size="lg">
          <Modal.Dialog>
            {({ close }) => (
              <>
                <Modal.Header>
                  <Modal.Heading>下载模板</Modal.Heading>
                </Modal.Header>
                <Modal.Body className="flex flex-col gap-2.5 md:flex-row">
                  <DownloadComponent
                    title="下载PSD格式"
                    description="适用于PC端和部分移动端绘画软件，保留图层效果和图层结构"
                    onDownload={() => handleDownload("psd")}
                    disabled={
                      !agreeTerms.terms || !agreeTerms.responsibility || isDownloading !== null
                    }
                    isLoading={isDownloading === "psd"}
                  />
                  <DownloadComponent
                    title="下载PNG格式"
                    description="适用于部分移动端绘画软件，可分层导入"
                    onDownload={() => handleDownload("png")}
                    disabled={
                      !agreeTerms.terms || !agreeTerms.responsibility || isDownloading !== null
                    }
                    isLoading={isDownloading === "png"}
                  />
                </Modal.Body>
                <Modal.Footer className="flex flex-col items-start justify-start gap-2.5">
                  <div className="flex w-full flex-col items-start justify-start gap-2.5">
                    <Checkbox
                      isSelected={agreeTerms.terms}
                      onChange={(checked) => setAgreeTerms({ ...agreeTerms, terms: checked })}
                    >
                      <Checkbox.Content>
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        <span className="line-clamp-3 text-sm leading-tight">
                          我遵守模板说明上的模板使用规定，不得将模板用于规定以外的用途。
                        </span>
                      </Checkbox.Content>
                    </Checkbox>
                    <Checkbox
                      isSelected={agreeTerms.responsibility}
                      onChange={(checked) =>
                        setAgreeTerms({
                          ...agreeTerms,
                          responsibility: checked,
                        })
                      }
                    >
                      <Checkbox.Content>
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        <span className="line-clamp-3 text-sm leading-tight">
                          我明白模板上含有与帐号绑定的数字信息，如违规传播将视情况承担封禁或赔偿责任。
                        </span>
                      </Checkbox.Content>
                    </Checkbox>
                  </div>
                  <Button
                    className="self-end rounded-full bg-gray-100 text-[#333333]"
                    onPress={close}
                    isDisabled={isDownloading !== null}
                  >
                    关闭
                  </Button>
                </Modal.Footer>
              </>
            )}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
};
